import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Paper, Chip, CircularProgress } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { GetAccountingDocumentDocument, type AccountingDocument } from '@/generated/root-types';
import {
  accountingDocumentStateColors,
  translateAccountingDocumentState,
  translatePaymentState,
} from '@/utils/billingStateLabels';
import {
  detailBackButtonSx,
  detailHeaderRowWrapSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';
import { Download } from '@mui/icons-material';

export default function AccountingDocumentDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromTenantId = (location.state as { fromTenantId?: string })?.fromTenantId;

  const typeLabels: Record<string, string> = {
    INVOICE: t('invoices.docTypeInvoice'),
    CREDIT_NOTE: t('invoices.docTypeCreditNote'),
    DEBIT_NOTE: t('invoices.docTypeDebitNote'),
    RECEIPT: t('invoices.docTypeReceipt'),
  };

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatCurrency = (amount?: number | null, currency?: string | null) =>
    amount != null
      ? new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'EUR' }).format(
          amount,
        )
      : '–';
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';

  const { data, loading, error } = useQuery(GetAccountingDocumentDocument, {
    variables: { id: id || '' },
    skip: !id,
  });
  const handleDownloadPdf = (accountingDocument: AccountingDocument) => {
    if (!accountingDocument.pdfDataBase64 || accountingDocument.pdfDataBase64.trim() === '') {
      alert(t('NoFileAvailableForDownload'));
      return;
    }

    const formattedDate = formatDate(accountingDocument.createdAt) || '';
    const filename = `${accountingDocument.number}_${formattedDate.replace(/[^0-9]/g, '')}.pdf`;
    const downloadLink = document.createElement('a');
    downloadLink.href = `data:application/pdf;base64,${accountingDocument.pdfDataBase64}`;
    downloadLink.download = filename;
    downloadLink.click();
    URL.revokeObjectURL(downloadLink.href);
  };

  const doc = data?.accountingDocument;

  const handleBack = () => {
    if (fromTenantId) navigate(`/customers/${fromTenantId}`);
    else navigate('/invoices');
  };

  if (loading) {
    return (
      <Box
        sx={{
          ...detailPageContainerSx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !doc) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          {t('common.back')}
        </Button>
        <Typography color="error">{error?.message ?? t('errors.invoiceNotFound')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={{ ...detailHeaderRowWrapSx, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={detailBackButtonSx}>
          {t('common.back')}
        </Button>
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
          <Typography variant="h4" component="h1" sx={detailTitleSx}>
            {typeLabels[doc.type] ?? doc.type} {doc.number}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="error"
          size="small"
          endIcon={<Download />}
          onClick={() => handleDownloadPdf(data?.accountingDocument as AccountingDocument)}
          sx={{ fontWeight: 1000 }}
        >
          {t('PDF')}
        </Button>
        <Chip
          label={translateAccountingDocumentState(t, doc.state)}
          color={accountingDocumentStateColors[doc.state] || 'default'}
          size="small"
        />
        {(fromTenantId ?? doc.subscription?.organizationUnit?.organization?.id) && (
          <Button
            size="small"
            onClick={() =>
              navigate(
                `/customers/${fromTenantId ?? doc.subscription?.organizationUnit?.organization?.id}`,
              )
            }
          >
            {t('common.toCustomer')}
          </Button>
        )}
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('organizationDetail.masterData')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('customers.customerOrg')}
            </Typography>
            <Typography variant="body2">
              {doc.subscription?.organizationUnit?.organization?.name ?? doc.tenant?.name ?? '–'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.type')}
            </Typography>
            <Typography variant="body2">{typeLabels[doc.type] ?? doc.type}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.status')}
            </Typography>
            <Typography variant="body2">
              {translateAccountingDocumentState(t, doc.state)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('invoices.subscription')}
            </Typography>
            <Typography variant="body2">{doc.subscription?.number ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.created')}
            </Typography>
            <Typography variant="body2">{formatDate(doc.createdAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.updated')}
            </Typography>
            <Typography variant="body2">{formatDate(doc.updatedAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.sent')}
            </Typography>
            <Typography variant="body2">{formatDate(doc.sentAt)}</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('invoices.amounts')}
        </Typography>
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.net')}
            </Typography>
            <Typography variant="body1">
              {formatCurrency(doc.amountNet, doc.currencyCode)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.gross')}
            </Typography>
            <Typography variant="body1">
              {formatCurrency(doc.amountGross, doc.currencyCode)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.vat')}
            </Typography>
            <Typography variant="body1">
              {formatCurrency(doc.taxAmount, doc.currencyCode)}
            </Typography>
          </Box>
        </Box>
        {(doc.documentHeader || doc.documentFooter || doc.privateNotice || doc.publicNotice) && (
          <Box sx={{ mt: 2 }}>
            {doc.documentHeader && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {doc.documentHeader}
              </Typography>
            )}
            {doc.publicNotice && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {doc.publicNotice}
              </Typography>
            )}
            {doc.privateNotice && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {doc.privateNotice}
              </Typography>
            )}
            {doc.documentFooter && (
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {doc.documentFooter}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {doc.items && doc.items.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('subscriptions.positions')}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {doc.items.map((item) => (
              <li key={item.id}>
                <Typography variant="body2">
                  {item.name} · {t('common.quantity')}: {item.quantity} ·{' '}
                  {formatCurrency(item.unitPriceGross)} = {formatCurrency(item.amountGross)}
                </Typography>
              </li>
            ))}
          </Box>
        </Paper>
      )}

      {doc.payments && doc.payments.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('tenantDetail.payments')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {doc.payments.map((p) => (
              <Box
                key={p.id}
                sx={{
                  border: 1,
                  p: 1,
                  borderRadius: 1,
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.main' },
                }}
                onClick={() =>
                  navigate(`/payments/${p.id}`, { state: { fromAccountingDocumentId: doc.id } })
                }
              >
                <Typography variant="body2">
                  {formatCurrency(p.amountGross)} · {formatDate(p.paymentDate ?? p.createdAt)} ·{' '}
                  {translatePaymentState(t, p.state)}
                  {p.paymentGateway?.name && ` · ${p.paymentGateway.name}`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
}
